import axios, { AxiosError } from 'axios';

import { ErrorResponse } from '@rygen/rygen-ui/model';

// TODO: Move this to rygen ui

export type FieldErrors = Record<string, string[]>;

export const isBadRequest = (e: unknown): e is AxiosError<unknown, any> => {
  return axios.isAxiosError(e) && e.response?.status === 400;
};

export const isConflict = (e: unknown): e is AxiosError<unknown, any> => {
  return axios.isAxiosError(e) && e.response?.status === 409;
};

export const getErrorResponse = (e: AxiosError) => {
  return e.response?.data as ErrorResponse;
};

export const getErrorDetail = (e: AxiosError, key: string): unknown => {
  const errorResponse = getErrorResponse(e);
  return errorResponse.errorDetails?.[key];
};

export const getFieldErrors = (errorResponse: ErrorResponse): FieldErrors => {
  const fieldErrors = errorResponse.errorDetails?.fieldErrors ?? {};
  // The the type in Rygen UI is wrong; backend is returning
  // `Record<string, string[]>` not `Record<string, FieldError | FieldError[]>`
  return fieldErrors as unknown as FieldErrors;
};

export const flattenFieldErrors = (fieldErrors: FieldErrors): string[] => {
  return Object.values(fieldErrors).flat();
};

export const getFlattenedFieldErrors = (
  errorResponse: ErrorResponse,
): string[] => {
  const fieldErrors = getFieldErrors(errorResponse);
  return flattenFieldErrors(fieldErrors);
};

/**
 * Requests made with `responseType: 'arraybuffer'` or `'blob'` (e.g. document downloads) receive their error body
 * as raw bytes. Replaces that body with the parsed JSON {@link ErrorResponse} so callers (and the toast's `spanId`
 * lookup) can read it like any other API error. Leaves the error untouched if the body isn't JSON.
 */
export const decodeBinaryErrorResponse = async (e: unknown): Promise<void> => {
  if (!axios.isAxiosError(e) || !e.response) {
    return;
  }

  const data: unknown = e.response.data;
  let text: string;
  // A tag check rather than `instanceof`, which fails for buffers created in another realm.
  if (Object.prototype.toString.call(data) === '[object ArrayBuffer]') {
    text = new TextDecoder().decode(data as ArrayBuffer);
  } else if (data instanceof Blob) {
    text = await data.text();
  } else {
    return;
  }

  try {
    e.response.data = JSON.parse(text);
  } catch {
    // Not a JSON error body; leave it as-is.
  }
};
